import { faExpand } from '@fortawesome/pro-solid-svg-icons';
import { getFieldValue as getNestedFieldValue } from '@supply-mover-2-0/core';
import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../../../components/base/Icon';
import { Tooltip } from '../../../components/overlays/Tooltip';
import { useTheme, useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { cn } from '../../../utils/cn';
import { useListLinkNavigation } from '../../hooks/useListLinkNavigation';
import type {
	EnhancedFieldConfiguration,
	ListBuilderCallbacks,
} from '../../types';
import { SIZE_MAP } from '../../utils/colorRuleEngine';
import { TextExpansionModal } from './TextExpansionModal';
import {
	getFieldDisplayProperties,
	resolveTextDisplayColor,
} from './displayHelpers';
import { handleTruncationTooltip } from './truncationTooltip';

/**
 * Check if a URL template can be fully resolved with the given data.
 * Returns false if any placeholder would resolve to null/undefined/empty.
 */
function canResolveUrlTemplate(
	template: string | undefined,
	data: Record<string, unknown>,
): boolean {
	if (template == null) {
		return false;
	}

	const matches = template.match(/\{([^}]+)\}/g);

	if (matches == null) {
		return true; // No placeholders, template is valid as-is
	}

	for (const match of matches) {
		const fieldPath = match.slice(1, -1);
		const value = getNestedFieldValue(data, fieldPath);

		if (value == null || String(value) === '') {
			return false;
		}
	}

	return true;
}

interface TextDisplayProps {
	field: EnhancedFieldConfiguration;
	value: any;
	data: any;
	listKey?: string;
	objects?: Array<{ objectTable: string; baseUrl?: string }>;
	callbacks?: ListBuilderCallbacks;
}

export function TextDisplay({
	field,
	value,
	data,
	listKey,
	objects,
	callbacks,
}: TextDisplayProps) {
	const colors = useThemeColors();
	const { isDark } = useTheme();
	const { t } = useTranslation();
	const { styleConfig, link } = field;
	const [isExpansionModalOpen, setIsExpansionModalOpen] = useState(false);

	// Use the shared link navigation hook
	const { linkUrl, handleLinkClick, isCallback } = useListLinkNavigation({
		field,
		data,
		listKey,
		objects,
		callbacks,
	});

	// Get display value and color using value mapping
	const { displayValue, colorValue } = getFieldDisplayProperties(
		field,
		value,
		data,
	);

	// Add prefix and suffix if configured, but only when there's an actual value
	// (not when displaying the default value)
	const prefix = field.prefix ?? '';
	const suffix = field.suffix ?? '';
	const defaultValue = field.dataConfig?.defaultValue ?? '';
	const isDefaultValue = displayValue === defaultValue;
	const formattedValue =
		displayValue != null && displayValue !== '' && !isDefaultValue
			? `${prefix}${displayValue}${suffix}`
			: displayValue;

	// Text expansion configuration. formatAsJson / showFormattedInModal are
	// forward-compatible knobs read here but not (yet) part of the
	// StyleConfiguration.textExpansion type; view through a wider shape.
	const textExpansion:
		| {
				enabled: boolean;
				maxLines?: number;
				formatAsJson?: boolean;
				showFormattedInModal?: boolean;
		  }
		| undefined = styleConfig?.textExpansion;
	const maxLines = textExpansion?.enabled ? textExpansion?.maxLines || 2 : 1;

	// Character thresholds based on maxLines
	// For line-clamp-1, we use a very low threshold because URLs and long text
	// can wrap unpredictably, causing visual truncation even with fewer characters
	const CHARACTER_THRESHOLDS = {
		1: 20, // Very conservative for single line - URLs truncate unpredictably
		2: 60, // ~60 chars for 2 lines
		3: 120, // ~120 chars for 3 lines
	};

	const charThreshold =
		CHARACTER_THRESHOLDS[maxLines as keyof typeof CHARACTER_THRESHOLDS] ||
		CHARACTER_THRESHOLDS[2];

	// Format as JSON if configured
	const formatAsJson = textExpansion?.formatAsJson ?? false;
	const showFormattedInModal = textExpansion?.showFormattedInModal ?? false;

	// For table display: show unformatted if showFormattedInModal is true, otherwise format
	const tableValue =
		formatAsJson && typeof displayValue === 'object' && displayValue != null
			? showFormattedInModal
				? JSON.stringify(displayValue)
				: JSON.stringify(displayValue, null, 2)
			: formattedValue;

	// For modal display: always show formatted if formatAsJson is true
	const modalValue =
		formatAsJson && typeof displayValue === 'object' && displayValue != null
			? JSON.stringify(displayValue, null, 2)
			: formattedValue;

	// Only the formatted-JSON modal still goes by length: see expansionEnabled.
	const textLength = String(displayValue || '').length;
	const isLongEnoughForExpansion = textLength > charThreshold;

	/**
	 * A single-line bound describes an ordinary one-line value that happens to
	 * be too long for its column — a product name, not the paragraph-style
	 * content an expand-to-modal control is for. It also makes the character
	 * heuristic above nearly useless: the threshold is 20 characters, so the
	 * icon appeared beside plenty of names that were not truncated at all.
	 * Those carry a tooltip with the full value instead, and only when the text
	 * really is clipped (PD-15805).
	 */
	const singleLineExpansion = textExpansion?.enabled === true && maxLines === 1;

	// Get size configuration - fallback to 'md' if size key is not found in SIZE_MAP
	const sizeConfig = SIZE_MAP[styleConfig?.size ?? 'md'] ?? SIZE_MAP.md;

	/**
	 * Whether the value is actually clipped right now, which is what decides
	 * whether the tooltip below has anything worth saying.
	 *
	 * Kept current rather than measured on hover: the tooltip reads this the
	 * instant the pointer arrives, so a measurement taken from that same event
	 * lands a render too late and the first hover never shows anything. An
	 * effect with no dependency list re-measures after every render (two
	 * property reads), and the observer covers a column being resized without
	 * one.
	 */
	const [isClipped, setIsClipped] = useState(false);
	// Every expansion field is measured, one line or several: the tooltip and
	// the expand control both depend on whether the text is really clipped.
	const measuresClipping = textExpansion?.enabled === true;
	const textRef = useRef<HTMLElement | null>(null);
	// A callback ref, because the same ref is attached to an anchor, a button
	// or a span depending on the branch, and a typed RefObject can only be one.
	const setTextRef = useCallback((el: HTMLElement | null): void => {
		textRef.current = el;
	}, []);

	useEffect(() => {
		const el = textRef.current;

		if (el == null) {
			return;
		}

		const measure = (): void => {
			// A pixel of slack on the height: a clamped block that shows all of
			// its text can still report a scroll height one pixel over.
			const next =
				el.scrollWidth > el.clientWidth ||
				el.scrollHeight - el.clientHeight > 1;
			setIsClipped((prev) => (prev === next ? prev : next));
		};

		measure();

		const observer = new ResizeObserver(measure);
		observer.observe(el);

		return (): void => {
			observer.disconnect();
		};
	});

	/**
	 * Show the expand control for MULTI-line content only when part of the text
	 * is hidden. It used to go by a character count (over 60 characters for two
	 * lines), so a note that fit in its two lines in a wide column still got a
	 * control that opened nothing new (PD-16477).
	 *
	 * The formatted-JSON modal is the exception: it shows the value laid out
	 * differently from the cell, so it is worth opening whether or not the cell
	 * clips, and keeps the length test.
	 */
	const opensFormattedModal = formatAsJson && showFormattedInModal;
	const expansionEnabled =
		textExpansion?.enabled === true &&
		!singleLineExpansion &&
		displayValue != null &&
		displayValue !== '' &&
		displayValue !== '-' &&
		(opensFormattedModal ? isLongEnoughForExpansion : isClipped);

	// Handle expansion modal
	const handleToggleExpansion = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			setIsExpansionModalOpen(!isExpansionModalOpen);
		},
		[isExpansionModalOpen],
	);

	// Helper to get record ID for link enabled check
	const getRecordId = (record: unknown, idField?: string): unknown => {
		if (record == null) {
			return null;
		}

		const rec = record as Record<string, unknown>;

		if (idField != null) {
			return rec[idField] ?? null;
		}

		return (
			rec['id'] ??
			rec['_id'] ??
			rec['Id'] ??
			rec['ID'] ??
			rec['clientId'] ??
			rec['client_id']
		);
	};

	// Check if this field has linking enabled
	const recordId = getRecordId(data, link?.idField);
	// For custom links with urlTemplate, verify all placeholders can be resolved
	const canResolveCustomUrl =
		link?.type === 'custom' &&
		(link.customUrl != null ||
			canResolveUrlTemplate(link.urlTemplate, data as Record<string, unknown>));

	const isLinkConfigured =
		link?.enabled &&
		(link.type === 'callback' ||
			(link.type === 'self' && recordId != null) ||
			canResolveCustomUrl ||
			(link.type === 'object' && recordId != null));

	// useListLinkNavigation may suppress the URL (e.g. for client-detail rows
	// the caller can't open). When that happens we want plain text — no link
	// styling, no underline, no clickable cursor — instead of the disabled
	// link-styled button fallback.
	const isLinkEnabled = isLinkConfigured && (isCallback || linkUrl != null);

	// Apply color and styles
	// For JSON content or text wrapping, we need to allow wrapping to prevent overflow
	const needsWrapping = formatAsJson && showFormattedInModal;
	const shouldWrap = styleConfig?.allowTextWrap || needsWrapping;

	// Single-line cells truncate with real `white-space: nowrap` + ellipsis
	// (`truncate`) instead of `line-clamp-1`. `line-clamp` uses
	// `display: -webkit-box`, which in Brave can mis-size a flex item
	// (`flex-1 min-w-0`) down to ~min-content and — together with
	// `word-break: break-all` — stack even short values like "ACH" vertically,
	// then clamp them to a phantom ellipsis with plenty of room left. `nowrap`
	// can't stack, so the value renders in full whenever it fits and only
	// truncates on genuine horizontal overflow. The wrapping cases (multi-line
	// clamp / allowTextWrap) set their own breaking rules below.
	const shouldClamp = textExpansion?.enabled || !styleConfig?.allowTextWrap;
	const isSingleLineTruncate = shouldClamp && maxLines === 1;
	const textStyle: React.CSSProperties = {
		color: resolveTextDisplayColor({
			isLinkEnabled: Boolean(isLinkEnabled),
			linkConfigEnabled: Boolean(link?.enabled),
			colorText: colorValue?.text,
			isDark,
			primaryColor: colors.values.link,
			defaultTextColor: colors.values.textPrimary,
		}),
		fontSize: sizeConfig.fontSize,
		lineHeight: 1.5,
		maxWidth: '100%',
		...(isSingleLineTruncate
			? {}
			: needsWrapping
				? {
						// Unformatted JSON has no word boundaries worth keeping.
						wordBreak: 'break-all',
						overflowWrap: 'break-word',
					}
				: {
						// Prose breaks BETWEEN words, whether the config asked it to
						// wrap (PD-16253) or it is clamped to a few lines, as a note
						// is (PD-16477: the notes list still split words, because
						// only the wrap case had been moved off `break-all`).
						// `anywhere` still splits a single over-long token, such as a
						// URL, so it cannot overflow.
						wordBreak: 'normal',
						overflowWrap: 'anywhere',
					}),
	};

	// Single-line EXPANSION fields (textExpansion.enabled + maxLines 1) render
	// nowrap text whose intrinsic width would stretch an auto-layout table
	// column to the full untruncated text (the visible table's `table-fixed`
	// class is inert without an explicit table width, so cells size by
	// content). width:0 + min-width:100% zeroes this field's min-content
	// contribution, letting the measured column width — and `truncate` —
	// actually apply. Scoped to the expansion case so the default text path
	// (also single-line) keeps its long-standing layout behavior.
	const singleLineExpansionContainerStyle: React.CSSProperties | undefined =
		textExpansion?.enabled === true && maxLines === 1
			? { width: 0, minWidth: '100%' }
			: undefined;

	// Build the link URL for the expansion modal if linking is enabled
	const expansionLinkUrl =
		isLinkEnabled && link?.type === 'custom' ? link.customUrl : undefined;

	// Render expansion modal
	const expansionModal = expansionEnabled && (
		<TextExpansionModal
			isOpen={isExpansionModalOpen}
			onClose={() => {
				setIsExpansionModalOpen(false);
			}}
			title={t('Full Text')}
			text={String(modalValue)}
			linkUrl={expansionLinkUrl}
			isCodeBlock={formatAsJson && showFormattedInModal}
		/>
	);

	/**
	 * Full value on hover for the single-line case, in place of the expand
	 * icon. Disabled unless the text is genuinely clipped, so a name that fits
	 * gets no tooltip at all. Uses the shared Tooltip rather than a native
	 * `title` (PD-15805).
	 */
	const withFullValueTooltip = (node: ReactNode): ReactNode =>
		singleLineExpansion ? (
			<Tooltip
				content={formatAsJson ? String(tableValue) : t(String(tableValue))}
				disabled={!isClipped}
				// A flex container, not a plain block: `truncate` sets
				// `overflow: hidden`, which only applies to a block-level box.
				// The text was a flex item before this wrapper existed, and
				// without `flex` here it goes back to being inline, stops
				// ellipsing, and spills past its column instead.
				className="min-w-0 flex-1 flex"
				delay={300}
			>
				{node}
			</Tooltip>
		) : (
			node
		);

	// Single line → `truncate` (nowrap + ellipsis); multi-line → `line-clamp-N`.
	const lineClampClass =
		maxLines === 1
			? 'truncate'
			: maxLines === 2
				? 'line-clamp-2'
				: 'line-clamp-3';
	const textClasses = cn(
		shouldClamp ? lineClampClass : '',
		needsWrapping ? 'overflow-hidden' : '',
	);

	if (isLinkEnabled) {
		// Callback links have no URL — render as button; navigation links render as anchor
		const linkContent = formatAsJson
			? String(tableValue)
			: t(String(tableValue));

		const linkElement =
			!isCallback && linkUrl != null ? (
				<a
					href={linkUrl}
					ref={measuresClipping ? setTextRef : undefined}
					onClick={handleLinkClick}
					onMouseEnter={
						singleLineExpansion ? undefined : handleTruncationTooltip
					}
					className={cn(
						textClasses,
						'hover:underline transition-all cursor-pointer',
					)}
					style={textStyle}
				>
					{linkContent}
				</a>
			) : (
				<button
					ref={measuresClipping ? setTextRef : undefined}
					onClick={handleLinkClick}
					onMouseEnter={
						singleLineExpansion ? undefined : handleTruncationTooltip
					}
					className={cn(
						textClasses,
						'hover:underline transition-all cursor-pointer text-left',
					)}
					style={textStyle}
					type="button"
				>
					{linkContent}
				</button>
			);

		return (
			<div
				className="flex items-start justify-between min-w-0 gap-2"
				style={singleLineExpansionContainerStyle}
			>
				{withFullValueTooltip(linkElement)}
				{expansionEnabled === true ? (
					<button
						onClick={handleToggleExpansion}
						className="py-0.5 hover:opacity-70 transition-opacity flex-shrink-0"
						type="button"
					>
						<Icon
							icon={faExpand}
							size={12}
							color={colors.values.textSecondary}
						/>
					</button>
				) : null}
				{expansionModal}
			</div>
		);
	}

	return (
		<div
			className="flex items-start justify-between min-w-0 gap-2"
			style={singleLineExpansionContainerStyle}
		>
			{withFullValueTooltip(
				<span
					ref={measuresClipping ? setTextRef : undefined}
					className={cn(textClasses, 'min-w-0 flex-1')}
					style={textStyle}
					onMouseEnter={
						singleLineExpansion ? undefined : handleTruncationTooltip
					}
				>
					{formatAsJson ? String(tableValue) : t(String(tableValue))}
				</span>,
			)}
			{expansionEnabled && (
				<button
					onClick={handleToggleExpansion}
					className="py-0.5 hover:opacity-70 transition-opacity flex-shrink-0"
					type="button"
				>
					<Icon icon={faExpand} size={12} color={colors.values.textSecondary} />
				</button>
			)}
			{expansionModal}
		</div>
	);
}
